import React, { useEffect, useRef, useState, useCallback } from "react";
import {
    Box,
    Paper,
    Typography,
    Chip,
    CircularProgress,
    Tooltip,
    Alert,
    Snackbar,
    IconButton,
    Divider,
    Avatar,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Checkbox,
    FormControlLabel,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    useMediaQuery,
    useTheme
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import VisibilityIcon from '@mui/icons-material/Visibility';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import DrawIcon from '@mui/icons-material/Draw';
import RateReviewIcon from '@mui/icons-material/RateReview';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import VerifiedIcon from '@mui/icons-material/Verified';
import RightPanel from "./RightPanel/RightPanel";
import DocumentEditor from "./DocumentEditor/DocumentEditor";
import { useLocation, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchContract, updateAlertStatus, createAmendment, generateAISuggestion } from "../../redux/actions/contractAction";
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import GavelIcon from '@mui/icons-material/Gavel';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EditorContext from "../../contexts/EditorContext";
import { useTranslation } from "react-i18next";
import { useSnackbar } from 'notistack';

// Import CSS for proper rendering of contract content
import BackButton from "../../components/BackButton";
import SigningDialog from "./dialogs/SigningDialog";
import ContractLocationBreadcrumb from "./components/ContractLocationBreadcrumb";
import { backendClient } from "../../api/backend";
import useContractTour from "./tour/useContractTour";

const ContractEditionScreen = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const editorRef = useRef(null);
    const dispatch = useDispatch();
    const { contractId } = useParams();
    const { enqueueSnackbar } = useSnackbar();

    const contract = useSelector((state) => state.contract.contract);
    const loading = useSelector((state) => state.contract.loading);
    const currentUser = useSelector((state) => state.auth.user.user);

    const [userRole, setUserRole] = useState('viewer');
    const [canEdit, setCanEdit] = useState(false);
    const [rightPanelOpen, setRightPanelOpen] = useState(!isMobile);
    const [notification, setNotification] = useState({
        open: false,
        message: '',
        severity: 'info'
    });

    // Internal signing state
    const [pendingSignatureInfo, setPendingSignatureInfo] = useState(null);
    const [signingDialogOpen, setSigningDialogOpen] = useState(false);

    // Reviewer state
    const [reviewStatus, setReviewStatus] = useState(null);
    const [completingReview, setCompletingReview] = useState(false);

    // Regulatory alert state
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const alertId = searchParams.get('alertId');
    const [activeAlert, setActiveAlert] = useState(null);
    const [activeAnchor, setActiveAnchor] = useState(null);
    const [generatingSuggestion, setGeneratingSuggestion] = useState(false);
    // Fetch contract data when component mounts
    useEffect(() => {
        if (contractId) {
            dispatch(fetchContract(contractId));
        }
    }, [contractId, dispatch]);

    // Fetch regulatory alert details + its anchor when alertId is in URL.
    // The anchor endpoint returns one of four strategies:
    //   citation_id   → flag existing LawCitation marks by their citationId
    //   exact_quote   → substring search for one deterministic quote
    //   llm_offset    → plain-text offsets from the backend LLM locator
    //   manual_required → show "click the clause" UI (TODO)
    useEffect(() => {
        if (!alertId || !contractId) return;
        let cancelled = false;
        Promise.all([
            backendClient.get(`api/contract/monitoring/regulatory-alerts/${contractId}`).then(r => r.json()),
            backendClient.get(`api/contract/monitoring/regulatory-alerts/${contractId}/${alertId}/anchor`).then(r => r.json()),
        ])
            .then(([alerts, anchor]) => {
                if (cancelled) return;
                const alert = alerts.find(a => a.id === alertId);
                if (alert) setActiveAlert(alert);
                if (anchor) setActiveAnchor(anchor);
            })
            .catch(err => console.error('Error fetching alert/anchor:', err));
        return () => { cancelled = true; };
    }, [alertId, contractId]);

    // Apply the anchor to the editor once both it and the editor are ready.
    useEffect(() => {
        if (!activeAnchor || !editorRef.current) return;

        const editor = editorRef.current.getEditor?.();
        if (!editor) return;

        const applyAnchor = () => {
            const doc = editor.state?.doc;
            if (!doc || doc.textContent.length < 10) return false;

            const strategy = activeAnchor.strategy;

            if (strategy === 'citation_id' && Array.isArray(activeAnchor.citation_ids) && activeAnchor.citation_ids.length > 0) {
                editor.commands.flagLawCitations(activeAnchor.citation_ids);
                setTimeout(() => {
                    const el = document.querySelector('.law-citation-flagged');
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 200);
                return true;
            }

            if (strategy === 'exact_quote' && activeAnchor.quote) {
                editor.commands.highlightMultipleTexts([activeAnchor.quote], '#c9a96e');
                setTimeout(() => {
                    const el = document.querySelector('.risk-highlight');
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 200);
                return true;
            }

            if (strategy === 'llm_offset' && activeAnchor.quote) {
                // LLM returns plain-text offsets, but since we already have the
                // quoted excerpt, substring search is the simplest applier.
                editor.commands.highlightMultipleTexts([activeAnchor.quote], '#c9a96e');
                setTimeout(() => {
                    const el = document.querySelector('.risk-highlight');
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 200);
                return true;
            }

            // manual_required — no automatic placement. The banner already
            // shows; UX to capture a user click is out of scope for this
            // first cut.
            return true;
        };

        if (!applyAnchor()) {
            let attempts = 0;
            const interval = setInterval(() => {
                attempts++;
                if (applyAnchor() || attempts > 20) {
                    clearInterval(interval);
                }
            }, 500);
            return () => clearInterval(interval);
        }
    }, [activeAnchor]);

    // Determine user permissions when contract data loads
    const isExternal = currentUser?.account_type === 'external';
    const subscription = useSelector((state) => state.subscription?.data);

    // Compute locked tabs for external users
    const lockedTabs = (() => {
        if (!isExternal) return [];
        const isTrialActive = subscription?.status === 'trial' &&
            subscription?.trial_end && new Date(subscription.trial_end) > new Date();
        const hasActiveSub = subscription?.status === 'active';
        if (!isTrialActive && !hasActiveSub) {
            // Post-trial: all locked except history
            return ['chatBot', 'riskAnalysis', 'explanation', 'suggestion', 'collaboration', 'signature'];
        }
        // Active trial: lock AI tabs on shared contracts
        return ['chatBot', 'riskAnalysis', 'explanation', 'suggestion', 'signature'];
    })();

    // Shepherd.js guided tour for external users
    useContractTour(isExternal, !loading && !!contract);

    useEffect(() => {
        if (contract && currentUser) {
            const collaborators = contract.collaborators || [];
            const userCollaborator = collaborators.find(
                collab => collab.id === currentUser.id
            );

            const role = userCollaborator?.role || 'viewer';
            setUserRole(role);

            // External users always get editor access in suggest mode (review mode)
            // Standard users: only editor, reviewer, and creator can edit
            const hasEditPermission = isExternal
                ? true  // External users get suggest-mode editor access
                : ['editor', 'reviewer', 'creator'].includes(role);
            setCanEdit(hasEditPermission);

            // Track review status for reviewers
            if (role === 'reviewer' || isExternal) {
                setReviewStatus(userCollaborator?.review_status || 'pending');
            }

            // Show notification for view-only users (not external — they get a different banner)
            if (!hasEditPermission && !isExternal) {
                setNotification({
                    open: true,
                    message: t("view_only_access_message"),
                    severity: 'info'
                });
            }
        }
    }, [contract, currentUser, t, isExternal]);

    // Check if user is a pending signer on this contract
    useEffect(() => {
        if (contractId && currentUser?.id) {
            backendClient.checkPendingSigner(contractId)
                .then(result => {
                    if (result.is_pending_signer) {
                        setPendingSignatureInfo(result.signature_info);
                    }
                })
                .catch(err => console.error('Error checking pending signer:', err));
        }
    }, [contractId, currentUser?.id]);

    // Update right panel visibility on mobile/desktop change
    useEffect(() => {
        setRightPanelOpen(!isMobile);
    }, [isMobile]);

    const handleCloseNotification = () => {
        setNotification({ ...notification, open: false });
    };

    const toggleRightPanel = () => {
        setRightPanelOpen(!rightPanelOpen);
    };

    const handleCompleteReview = async () => {
        setCompletingReview(true);
        try {
            await backendClient.completeReview(contractId);
            setReviewStatus('completed');
            enqueueSnackbar(t('review_completed', 'Relecture terminée'), { variant: 'success' });
        } catch (err) {
            enqueueSnackbar(err.message || t('review_complete_error', 'Erreur lors de la validation'), { variant: 'error' });
        } finally {
            setCompletingReview(false);
        }
    };

    // Regulatory alert actions
    const handleDismissAlert = async () => {
        if (!activeAlert) return;
        try {
            await dispatch(updateAlertStatus(activeAlert.id, 'dismissed'));
            setActiveAlert(null);
            enqueueSnackbar(t('monitoring.alertDismissed', 'Alert dismissed'), { variant: 'info' });
        } catch (err) {
            enqueueSnackbar(t('monitoring.alertError', 'Error updating alert'), { variant: 'error' });
        }
    };

    const handleAcknowledgeAlert = async () => {
        if (!activeAlert) return;
        try {
            await dispatch(updateAlertStatus(activeAlert.id, 'reviewed'));
            setActiveAlert(null);
            enqueueSnackbar(t('monitoring.alertReviewed', 'Alert acknowledged'), { variant: 'success' });
        } catch (err) {
            enqueueSnackbar(t('monitoring.alertError', 'Error updating alert'), { variant: 'error' });
        }
    };

    const handleCreateAmendment = async () => {
        try {
            const amendment = await dispatch(createAmendment(contractId));
            if (activeAlert) {
                await dispatch(updateAlertStatus(activeAlert.id, 'reviewed'));
            }
            enqueueSnackbar(t('monitoring.amendmentCreated', 'Amendment created'), { variant: 'success' });
            navigate(`/contract/${amendment.id}`);
        } catch (err) {
            enqueueSnackbar(err.message || t('monitoring.amendmentError', 'Error creating amendment'), { variant: 'error' });
        }
    };

    // Multi-clause dialog state
    const [clauseDialogOpen, setClauseDialogOpen] = useState(false);
    const [detectedClauses, setDetectedClauses] = useState([]);
    const [selectedClauseIndices, setSelectedClauseIndices] = useState([]);
    const [clauseGenerationProgress, setClauseGenerationProgress] = useState(0);

    // Scan the document for all paragraphs containing the law citation
    const scanDocumentForClauses = useCallback(() => {
        if (!activeAlert || !editorRef.current) return [];

        const editor = editorRef.current.getEditor?.();
        if (!editor) return [];

        const doc = editor.state.doc;
        const alertDocNum = String(activeAlert.doc_number || '');
        const clauses = [];
        if (!alertDocNum) return clauses;
        // Match the law number as a whole token ("50" must not match "850,000" or "No. 150")
        const escaped = alertDocNum.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const docNumRe = new RegExp(`(^|[^\\d.,/])${escaped}(?![\\d]|[.,]\\d)`, 'i');

        doc.descendants((node, pos) => {
            if (node.isTextblock) {
                const text = node.textContent;
                if (docNumRe.test(text) && text.length > 15) {
                    // Find the parent heading for context
                    let heading = '';
                    doc.nodesBetween(0, pos, (n) => {
                        if (n.type.name === 'heading') {
                            heading = n.textContent;
                        }
                    });

                    clauses.push({
                        text,
                        from: pos + 1,
                        // nodeSize covers inline non-text nodes (hard breaks…) that textContent skips
                        to: pos + node.nodeSize - 1,
                        heading: heading || '',
                        preview: text.length > 100 ? text.slice(0, 100) + '...' : text,
                    });
                }
                return false;
            }
            return true;
        });

        return clauses;
    }, [activeAlert]);

    // Generate AI suggestion for a single clause (from bubble or dialog)
    const generateSuggestionForClause = useCallback(async (clauseText, clauseFrom, clauseTo) => {
        if (!activeAlert || !editorRef.current) return false;

        const editor = editorRef.current.getEditor?.();
        if (!editor) return false;

        try {
            const suggestion = await dispatch(generateAISuggestion(contractId, activeAlert.id, clauseText));

            if (suggestion && suggestion.suggestedText && suggestion.originalText) {
                // Use addExternalSuggestion to apply marks AND add to hook state (renders the card)
                editorRef.current.addExternalSuggestion(suggestion, clauseFrom, clauseTo);
                return true;
            }
        } catch (err) {
            console.error('Error generating suggestion for clause:', err);
        }
        return false;
    }, [activeAlert, contractId, dispatch]);

    // Banner button: open multi-clause dialog
    const handleOpenClauseDialog = () => {
        const clauses = scanDocumentForClauses();
        if (clauses.length === 0) {
            enqueueSnackbar(t('monitoring.clauseNotFound', 'Could not find the affected clause in the document'), { variant: 'warning' });
            return;
        }
        if (clauses.length === 1) {
            // Only one clause — generate directly, no dialog needed
            handleGenerateSingleClause(clauses[0]);
            return;
        }
        setDetectedClauses(clauses);
        setSelectedClauseIndices(clauses.map((_, i) => i)); // All selected by default
        setClauseDialogOpen(true);
    };

    // Generate for a single clause (from widget click, bubble click, or dialog with 1 clause)
    const handleGenerateSingleClause = useCallback(async (clause) => {
        setGeneratingSuggestion(true);
        const success = await generateSuggestionForClause(clause.text, clause.from, clause.to);
        setGeneratingSuggestion(false);
        if (success) {
            enqueueSnackbar(t('monitoring.suggestionGenerated', 'AI suggestion generated — review the tracked change'), { variant: 'success' });
        } else {
            enqueueSnackbar(t('monitoring.suggestionError', 'Failed to generate suggestion'), { variant: 'error' });
        }
    }, [generateSuggestionForClause, enqueueSnackbar, t]);


    // Generate for selected clauses from the dialog
    const handleGenerateSelectedClauses = async () => {
        setClauseDialogOpen(false);
        setGeneratingSuggestion(true);
        setClauseGenerationProgress(0);

        const selected = selectedClauseIndices.map(i => detectedClauses[i]);
        let successCount = 0;

        // Generate sequentially (positions shift after each insertion)
        // Process in reverse order to avoid position offset issues
        const sorted = [...selected].sort((a, b) => b.from - a.from);
        for (let i = 0; i < sorted.length; i++) {
            const clause = sorted[i];
            const success = await generateSuggestionForClause(clause.text, clause.from, clause.to);
            if (success) successCount++;
            setClauseGenerationProgress(i + 1);
        }

        setGeneratingSuggestion(false);
        setClauseGenerationProgress(0);

        if (successCount > 0) {
            enqueueSnackbar(
                t('monitoring.suggestionsGenerated', '{{count}} AI suggestion(s) generated', { count: successCount }),
                { variant: 'success' }
            );
        } else {
            enqueueSnackbar(t('monitoring.suggestionError', 'Failed to generate suggestion'), { variant: 'error' });
        }
    };

    // Bubble click handler: generate for the selected text.
    // Returns the promise so the bubble can show a loading state until done.
    // A short selection (typically just the highlighted law citation) is expanded to the
    // whole paragraph: the AI rewrites the clause, and the suggestion replaces the clause
    // instead of dropping a sentence in the middle of a line.
    const handleBubbleAISuggest = useCallback(({ from, to, text }) => {
        if (!activeAlert) return;
        const editor = editorRef.current?.getEditor?.();
        if (editor && (text || '').trim().split(/\s+/).length < 8) {
            const $from = editor.state.doc.resolve(from);
            const $to = editor.state.doc.resolve(to);
            if ($from.parent.isTextblock && $from.sameParent($to)) {
                const start = $from.start();
                const end = $from.end();
                const paragraph = editor.state.doc.textBetween(start, end, ' ');
                return handleGenerateSingleClause({ text: paragraph, from: start, to: end });
            }
        }
        return handleGenerateSingleClause({ text, from, to });
    }, [activeAlert, handleGenerateSingleClause]);

    // Pass active alert + canEdit to DocumentEditor so the bubble shows AI suggest button
    const canEditContract = contract?.status !== 'SIGNED' && contract?.status !== 'PENDING_SIGNATURE';
    useEffect(() => {
        const apply = () => {
            if (!editorRef.current) return false;
            const alertData = activeAlert ? { ...activeAlert, canEdit: canEditContract } : null;
            editorRef.current.setActiveAlert?.(alertData);
            editorRef.current.setOnAISuggestClick?.(activeAlert && canEditContract ? handleBubbleAISuggest : null);
            return true;
        };

        if (!apply()) {
            // Editor ref not ready yet — retry until it is
            const interval = setInterval(() => {
                if (apply()) clearInterval(interval);
            }, 300);
            return () => clearInterval(interval);
        }
    }, [activeAlert, handleBubbleAISuggest]);

    const getRoleColor = (role) => {
        switch (role) {
            case 'editor':
                return theme.palette.info.main;
            case 'reviewer':
                return theme.palette.secondary.main;
            case 'creator':
                return theme.palette.success.main;
            case 'signer':
                return theme.palette.warning.main;
            default:
                return theme.palette.text.secondary;
        }
    };

    if (loading) {
        return (
            <Box sx={{
                backgroundColor: theme.palette.background.default,
                height: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
            }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>{t("loading_contract")}</Typography>
            </Box>
        );
    }

    // For editors, render only DocumentEditor without header/footer
    if (canEdit) {
        return (
            <Box sx={{ backgroundColor: theme.palette.background.default, height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", }}>
                <Paper elevation={3} sx={{
                    height: "100vh",
                    width: "100%",
                    padding: 0,
                    pr: 0,
                    display: "flex",
                    flexDirection: "column",
                    position: "relative", // Add position relative
                    overflow: "hidden" // Prevent content from overflowing
                }}>
                    <EditorContext.Provider value={editorRef}>
                        {/* Signing banner for editors who are also signatories */}
                        {pendingSignatureInfo && (
                            <Box sx={{
                                px: 2, py: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                backgroundColor: alpha(theme.palette.warning.main, 0.08),
                                borderBottom: `2px solid ${theme.palette.warning.main}`,
                                flexShrink: 0,
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <DrawIcon sx={{ color: theme.palette.warning.main, fontSize: 20 }} />
                                    <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                                        {t('signing.bannerMessage')}
                                    </Typography>
                                    {pendingSignatureInfo.expires_at && (
                                        <Chip
                                            label={t('signing.expiresOn', { date: new Date(pendingSignatureInfo.expires_at).toLocaleDateString() })}
                                            size="small"
                                            color="warning"
                                            variant="outlined"
                                        />
                                    )}
                                </Box>
                                <Button
                                    variant="contained"
                                    color="warning"
                                    size="small"
                                    startIcon={<DrawIcon />}
                                    onClick={() => setSigningDialogOpen(true)}
                                    sx={{ textTransform: 'none', fontWeight: 600 }}
                                >
                                    {t('signing.signNow')}
                                </Button>
                            </Box>
                        )}

                        {/* External user review banner */}
                        {isExternal && (
                            <Box sx={{
                                px: 2, py: 1.25,
                                display: 'flex',
                                alignItems: 'center',
                                backgroundColor: alpha('#6366f1', 0.08),
                                borderBottom: `2px solid #6366f1`,
                                flexShrink: 0,
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <RateReviewIcon sx={{ color: '#6366f1', fontSize: 20 }} />
                                    <Typography sx={{ fontWeight: 600, fontSize: '0.84rem' }}>
                                        {t('contract.externalReviewBanner', 'Vous révisez ce contrat en tant que collaborateur externe.')}
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        {/* Reviewer banner */}
                        {userRole === 'reviewer' && !isExternal && (
                            <Box sx={{
                                px: 2, py: 1.25,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                backgroundColor: alpha('#c9a96e', 0.08),
                                borderBottom: `2px solid #c9a96e`,
                                flexShrink: 0,
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <RateReviewIcon sx={{ color: '#c9a96e', fontSize: 20 }} />
                                    <Typography sx={{ fontWeight: 600, fontSize: '0.84rem' }}>
                                        {t('review_banner_message', 'Vous relisez ce contrat. Vos modifications seront suivies comme suggestions.')}
                                    </Typography>
                                </Box>
                                {reviewStatus === 'completed' ? (
                                    <Chip
                                        icon={<CheckCircleOutlineIcon sx={{ fontSize: 16 }} />}
                                        label={t('review_completed', 'Relecture terminée')}
                                        size="small"
                                        sx={{
                                            fontWeight: 600,
                                            backgroundColor: alpha('#10b981', 0.12),
                                            color: '#10b981',
                                            '& .MuiChip-icon': { color: '#10b981' },
                                            borderRadius: '8px',
                                        }}
                                    />
                                ) : (
                                    <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={completingReview ? <CircularProgress size={14} color="inherit" /> : <CheckCircleOutlineIcon />}
                                        onClick={handleCompleteReview}
                                        disabled={completingReview}
                                        sx={{
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            backgroundColor: '#c9a96e',
                                            color: '#1c1917',
                                            '&:hover': { backgroundColor: '#b8944f' },
                                            borderRadius: '8px',
                                        }}
                                    >
                                        {t('mark_review_complete', 'Marquer la relecture comme terminée')}
                                    </Button>
                                )}
                            </Box>
                        )}

                        {/* Pending signature banner */}
                        {contract?.status === 'PENDING_SIGNATURE' && !pendingSignatureInfo && (
                            <Box sx={{
                                px: 2, py: 1.25,
                                display: 'flex',
                                alignItems: 'center',
                                backgroundColor: alpha(theme.palette.warning.main, 0.08),
                                borderBottom: `2px solid ${theme.palette.warning.main}`,
                                flexShrink: 0,
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <DrawIcon sx={{ color: theme.palette.warning.main, fontSize: 20 }} />
                                    <Typography sx={{ fontWeight: 600, fontSize: '0.84rem', color: theme.palette.warning.main }}>
                                        {t('contract.pendingSignatureBanner', 'Ce contrat est en attente de signature.')}
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        {/* Signed contract banner */}
                        {contract?.status === 'SIGNED' && !activeAlert && (
                            <Box sx={{
                                px: 2, py: 1.25,
                                display: 'flex',
                                alignItems: 'center',
                                backgroundColor: alpha('#10b981', 0.08),
                                borderBottom: `2px solid #10b981`,
                                flexShrink: 0,
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <VerifiedIcon sx={{ color: '#10b981', fontSize: 20 }} />
                                    <Typography sx={{ fontWeight: 600, fontSize: '0.84rem', color: '#10b981' }}>
                                        {t('contract.signedBanner', 'Ce contrat a été signé par toutes les parties.')}
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        {/* Regulatory Alert Banner */}
                        {activeAlert && (
                            <Box sx={{
                                px: 2, py: 1.25,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                backgroundColor: alpha(theme.palette.warning.main, 0.08),
                                borderBottom: `2px solid ${theme.palette.warning.main}`,
                                flexShrink: 0,
                                flexWrap: 'wrap',
                                gap: 1,
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                                    <WarningAmberIcon sx={{ color: theme.palette.warning.main, fontSize: 20 }} />
                                    <Typography sx={{ fontWeight: 600, fontSize: '0.84rem' }}>
                                        {contract?.status === 'SIGNED'
                                            ? t('monitoring.signedAlertBanner', 'Signed contract — a regulatory change affects this document.')
                                            : t('monitoring.unsignedAlertBanner', 'A regulatory change affects this contract — review and update the highlighted clause.')}
                                    </Typography>
                                    <Chip
                                        label={`${activeAlert.doc_number}${activeAlert.article_ref ? ` — ${activeAlert.article_ref}` : ''}`}
                                        size="small"
                                        variant="outlined"
                                        sx={{ fontSize: '0.7rem', fontWeight: 600 }}
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    {contract?.status === 'SIGNED' && (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<GavelIcon />}
                                            onClick={handleCreateAmendment}
                                            sx={{
                                                textTransform: 'none', fontWeight: 600, fontSize: '0.75rem',
                                                borderColor: 'rgba(255,255,255,0.3)',
                                                color: '#fff',
                                                '&:hover': { borderColor: 'rgba(255,255,255,0.5)', bgcolor: 'rgba(255,255,255,0.08)' },
                                            }}
                                        >
                                            {t('monitoring.createAmendment', 'Create Amendment')}
                                        </Button>
                                    )}
                                    {canEditContract && (
                                        <Button
                                            size="small"
                                            variant="contained"
                                            startIcon={generatingSuggestion ? <CircularProgress size={14} color="inherit" /> : <AutoAwesomeIcon />}
                                            onClick={handleOpenClauseDialog}
                                            disabled={generatingSuggestion}
                                            sx={{
                                                textTransform: 'none', fontWeight: 600, fontSize: '0.75rem',
                                                bgcolor: '#c9a96e', color: '#1c1917',
                                                '&:hover': { bgcolor: '#b8944f' },
                                            }}
                                        >
                                            {generatingSuggestion
                                                ? t('monitoring.generatingSuggestion', 'Generating...')
                                                : t('monitoring.generateSuggestion', 'AI Suggestion')}
                                        </Button>
                                    )}
                                    {contract?.status !== 'SIGNED' && (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<CheckCircleOutlineIcon />}
                                            onClick={handleAcknowledgeAlert}
                                            sx={{
                                                textTransform: 'none', fontWeight: 600, fontSize: '0.75rem',
                                            }}
                                        >
                                            {t('monitoring.markReviewed', 'Mark Reviewed')}
                                        </Button>
                                    )}
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={contract?.status === 'SIGNED' ? handleAcknowledgeAlert : handleDismissAlert}
                                        sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem' }}
                                    >
                                        {contract?.status === 'SIGNED'
                                            ? t('monitoring.acknowledge', 'Acknowledge')
                                            : t('monitoring.dismiss', 'Dismiss')}
                                    </Button>
                                </Box>
                            </Box>
                        )}

                        <Box sx={{
                            display: "flex",
                            bgcolor: theme.palette.background.paper,
                            borderRadius: "8px",
                            height: "100%",
                            overflow: "hidden",
                        }}>
                            <Box sx={{
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                                overflow: "hidden",
                                position: "relative"
                            }}>
                                <DocumentEditor
                                    ref={editorRef}
                                    onContractMoved={() => contract?.id && dispatch(fetchContract(contract.id))}
                                />
                            </Box>

                            {rightPanelOpen && (
                                <>
                                    {/* <Divider orientation="vertical" flexItem /> */}

                                    <Box sx={{
                                        width: "400px",
                                        minWidth: "400px",
                                        flexShrink: 0,
                                        height: "100%",
                                        overflow: "hidden",
                                        borderLeft: `1px solid`,
                                        borderColor: 'divider',
                                    }}>
                                        <RightPanel
                                            contractId={contractId}
                                            editor={editorRef.current?.getEditor()}
                                            onBeforeDownload={() => editorRef.current?.saveBeforeDownload?.() ?? Promise.resolve()}
                                            lockedTabs={lockedTabs}
                                        />
                                    </Box>
                                </>
                            )}

                            {/* Floating toggle button for right panel */}
                            {!rightPanelOpen && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: '10px',
                                        right: '10px',
                                        zIndex: 100
                                    }}
                                >
                                    <IconButton
                                        size="small"
                                        onClick={toggleRightPanel}
                                        color="primary"
                                        sx={{
                                            bgcolor: theme.palette.background.paper,
                                            boxShadow: theme.palette.mode === 'dark' ? '0px 2px 4px rgba(0,0,0,0.3)' : '0px 2px 4px rgba(0,0,0,0.1)',
                                            '&:hover': { bgcolor: theme.palette.background.paper, boxShadow: theme.palette.mode === 'dark' ? '0px 3px 6px rgba(0,0,0,0.4)' : '0px 3px 6px rgba(0,0,0,0.15)' }
                                        }}
                                    >
                                        <MenuIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            )}
                        </Box>
                    </EditorContext.Provider>

                    <Snackbar
                        open={notification.open}
                        autoHideDuration={6000}
                        onClose={handleCloseNotification}
                        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    >
                        <Alert
                            onClose={handleCloseNotification}
                            severity={notification.severity}
                            sx={{ width: '100%' }}
                        >
                            {notification.message}
                        </Alert>
                    </Snackbar>

                    <SigningDialog
                        open={signingDialogOpen}
                        onClose={() => setSigningDialogOpen(false)}
                        contractId={contractId}
                        contractTitle={contract?.title}
                        onSigningComplete={() => {
                            setPendingSignatureInfo(null);
                            setSigningDialogOpen(false);
                            dispatch(fetchContract(contractId));
                        }}
                    />
                </Paper>
            </Box>
        );
    }

    // For viewers, render the complete interface with document display
    return (
        <Box sx={{
            backgroundColor: theme.palette.background.default,
            height: "95vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
        }}>
            <Paper
                elevation={3}
                sx={{
                    height: "95vh",
                    width: "100%",
                    padding: 0,
                    pr: 0,
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                    overflow: "hidden"
                }}
            >
                {/* Header for viewers */}
                <Box sx={{
                    p: 2,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: 'background.paper',
                    zIndex: 10
                }}>
                    <BackButton navigate_to={'/contract'} />

                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                        <Typography variant="h6" sx={{ fontWeight: 500 }}>
                            {contract?.title}
                        </Typography>
                        {contract?.id && (
                            <ContractLocationBreadcrumb
                                contractId={contract.id}
                                workspaceId={contract.workspace_id}
                                folderId={contract.folder_id}
                                workspaceName={contract.workspace_name}
                                folderName={contract.folder_name}
                                onMoved={() => dispatch(fetchContract(contract.id))}
                                variant="chip"
                            />
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Chip
                            icon={<VisibilityIcon fontSize="small" />}
                            label={t("read_only")}
                            size="small"
                            color="default"
                            variant="outlined"
                        />
                    </Box>
                </Box>

                {/* Signing banner for viewers who are signatories */}
                {pendingSignatureInfo && (
                    <Box sx={{
                        px: 2, py: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        backgroundColor: alpha(theme.palette.warning.main, 0.08),
                        borderBottom: `2px solid ${theme.palette.warning.main}`,
                        flexShrink: 0,
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DrawIcon sx={{ color: theme.palette.warning.main, fontSize: 20 }} />
                            <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                                {t('signing.bannerMessage')}
                            </Typography>
                            {pendingSignatureInfo.expires_at && (
                                <Chip
                                    label={t('signing.expiresOn', { date: new Date(pendingSignatureInfo.expires_at).toLocaleDateString() })}
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                />
                            )}
                        </Box>
                        <Button
                            variant="contained"
                            color="warning"
                            size="small"
                            startIcon={<DrawIcon />}
                            onClick={() => setSigningDialogOpen(true)}
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                            {t('signing.signNow')}
                        </Button>
                    </Box>
                )}

                {/* Contract content for viewers */}
                <Box sx={{
                    flex: 1,
                    overflow: "auto",
                    p: 3,
                    bgcolor: theme.palette.background.paper
                }}>
                    <Paper
                        elevation={0}
                        sx={{
                            maxWidth: 800,
                            mx: 'auto',
                            p: 4,
                            minHeight: '100%'
                        }}
                    >
                        <div
                            className="contract-content read-only"
                            dangerouslySetInnerHTML={{ __html: contract?.content || '' }}
                        />
                    </Paper>
                </Box>

                {/* Collaborators footer for viewers */}
                {contract?.collaborators && contract.collaborators.length > 0 && (
                    <Box sx={{
                        p: 1,
                        borderTop: `1px solid ${theme.palette.divider}`,
                        display: 'flex',
                        alignItems: 'center',
                        bgcolor: 'background.paper'
                    }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                            {t("collaborators")}:
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {contract.collaborators.map((collab, index) => (
                                <Tooltip
                                    key={collab.id || index}
                                    title={`${collab.first_name} ${collab.last_name} (${collab.role})`}
                                >
                                    <Avatar
                                        sx={{
                                            width: 28,
                                            height: 28,
                                            fontSize: '0.875rem',
                                            bgcolor: getRoleColor(collab.role)
                                        }}
                                    >
                                        {collab.first_name?.charAt(0) || '?'}
                                    </Avatar>
                                </Tooltip>
                            ))}
                        </Box>
                    </Box>
                )}
            </Paper>

            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={handleCloseNotification}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseNotification}
                    severity={notification.severity}
                    sx={{ width: '100%' }}
                >
                    {notification.message}
                </Alert>
            </Snackbar>

            <SigningDialog
                open={signingDialogOpen}
                onClose={() => setSigningDialogOpen(false)}
                contractId={contractId}
                contractTitle={contract?.title}
                onSigningComplete={() => {
                    setPendingSignatureInfo(null);
                    setSigningDialogOpen(false);
                    dispatch(fetchContract(contractId));
                }}
            />

            {/* Multi-clause AI suggestion dialog */}
            <Dialog
                open={clauseDialogOpen}
                onClose={() => setClauseDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '12px',
                        bgcolor: theme.palette.background.paper,
                    },
                }}
            >
                <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem', pb: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AutoAwesomeIcon sx={{ color: '#c9a96e', fontSize: 22 }} />
                        {t('monitoring.aiSuggestionTitle', 'AI Suggestion')}
                    </Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, fontSize: '0.8rem' }}>
                        {t('monitoring.clausesFoundDescription', 'Found {{count}} clause(s) referencing {{docNumber}}. Select which to update:', {
                            count: detectedClauses.length,
                            docNumber: activeAlert?.doc_number || '',
                        })}
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    <List dense disablePadding>
                        {detectedClauses.map((clause, idx) => (
                            <ListItem
                                key={idx}
                                sx={{
                                    borderRadius: '8px',
                                    mb: 0.5,
                                    border: '1px solid',
                                    borderColor: selectedClauseIndices.includes(idx)
                                        ? alpha('#c9a96e', 0.4)
                                        : 'divider',
                                    bgcolor: selectedClauseIndices.includes(idx)
                                        ? alpha('#c9a96e', 0.05)
                                        : 'transparent',
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                    <Checkbox
                                        size="small"
                                        checked={selectedClauseIndices.includes(idx)}
                                        onChange={(e) => {
                                            setSelectedClauseIndices(prev =>
                                                e.target.checked
                                                    ? [...prev, idx]
                                                    : prev.filter(i => i !== idx)
                                            );
                                        }}
                                        sx={{ '&.Mui-checked': { color: '#c9a96e' } }}
                                    />
                                </ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Typography sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                                            {clause.heading || `Clause ${idx + 1}`}
                                        </Typography>
                                    }
                                    secondary={
                                        <Typography
                                            sx={{
                                                fontSize: '0.75rem',
                                                color: 'text.secondary',
                                                mt: 0.25,
                                                lineHeight: 1.4,
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                            }}
                                        >
                                            {clause.preview}
                                        </Typography>
                                    }
                                />
                            </ListItem>
                        ))}
                    </List>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setClauseDialogOpen(false)}
                        sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                    >
                        {t('common.cancel', 'Cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleGenerateSelectedClauses}
                        disabled={selectedClauseIndices.length === 0}
                        startIcon={<AutoAwesomeIcon />}
                        sx={{
                            textTransform: 'none', fontWeight: 600, fontSize: '0.8rem',
                            bgcolor: '#c9a96e', color: '#1c1917',
                            '&:hover': { bgcolor: '#b8944f' },
                        }}
                    >
                        {t('monitoring.generateForSelected', 'Generate for {{count}} clause(s)', {
                            count: selectedClauseIndices.length,
                        })}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ContractEditionScreen;